import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import heroImg from "@/assets/illustrations/hero-collaboration.jpg";
import { lazy, Suspense } from "react";

// Lazy-load WebGL to keep it off the critical path
const WebGLTorusBackground = lazy(() => import("@/components/WebGLTorusBackground"));

const HeroSection = () => (
  <section className="relative min-h-[80vh] md:min-h-screen flex items-center pt-24 pb-12 md:pt-16 overflow-hidden">
    <img
      src={heroImg}
      alt="Team collaborating on citizen development with no-code tools"
      aria-hidden="true"
      fetchPriority="high"
      width={1200}
      height={800}
      className="absolute inset-0 w-full h-full object-cover opacity-[0.12] pointer-events-none"
    />

    <Suspense fallback={null}>
      <WebGLTorusBackground />
    </Suspense>

    <div className="absolute top-0 right-0 w-[400px] md:w-[700px] h-[400px] md:h-[700px] rounded-full bg-primary/5 blur-[150px] pointer-events-none" aria-hidden="true" />
    <div className="absolute bottom-0 left-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-primary/3 blur-[120px] pointer-events-none" aria-hidden="true" />

    <div className="container relative z-10">
      <div className="max-w-3xl mx-auto text-center">

        <h1
          className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 md:mb-8"
        >
          Automate Workflows.
          <br />
          Build Internal Tools.{" "}
          <span className="relative inline-block not-italic">
            <span className="relative z-10 text-primary">No Code.</span>
            <span
              className="absolute inset-0 z-0 animate-torus-glow rounded-lg bg-primary/30 blur-md will-change-transform"
              aria-hidden="true"
            />
          </span>
        </h1>

        <p
          className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-5 md:mb-6 mx-auto animate-fade-in-up animation-delay-150"
        >
          The open-source guide to no-code workflow automation, business process automation,
          and internal tool building for ops and IT leaders. Platform comparisons, governance
          playbooks, ROI templates — everything you need to ship without engineering.
        </p>

        <p
          className="text-xs md:text-sm text-muted-foreground mb-8 md:mb-12 animate-fade-in-up animation-delay-300"
        >
          <span className="font-semibold text-foreground">Retool · Zapier · Make · Airtable</span>
          {" · "}
          <span className="font-semibold text-foreground">Free playbooks & ROI calculators</span>
          {" · "}
          <span className="font-semibold text-foreground">Weekly automation newsletter</span>
        </p>

        <div
          className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center animate-fade-in-up animation-delay-300"
        >
          <Button variant="hero" size="lg" className="gap-2 w-full sm:w-auto" asChild>
            <Link to="/blog/launch-citizen-development-program-90-days">Start with the playbook <ArrowRight size={16} /></Link>
          </Button>
          <Button variant="hero-outline" size="lg" className="w-full sm:w-auto" asChild>
            <a href="#newsletter">Join the weekly newsletter</a>
          </Button>
        </div>

        <p
          className="mt-8 md:mt-14 text-xs md:text-sm text-muted-foreground max-w-lg leading-relaxed mx-auto animate-fade-in-up animation-delay-500"
        >
          Based on research from Gartner, Forrester, and real-world enterprise citizen
          development programs. Updated weekly with new guides and case studies.
        </p>
      </div>
    </div>
  </section>
);

export default HeroSection;
